<template>
  <v-container fluid class="pa-6">
    <v-card elevation="3" class="rounded-xl overflow-hidden">
      <!-- En-tête de la carte -->
      <v-sheet color="primary" class="pa-4 d-flex align-center">
        <i class="fas fa-tags white--text" style="font-size: 24px; margin-right: 12px;"></i>
        <span class="text-h6 white--text font-weight-bold">Categories Management</span>
      </v-sheet>

      <!-- Onglets -->
      <v-tabs v-model="tab" background-color="transparent" grow class="px-4 pt-2">
        <v-tab>Add Category</v-tab>
        <v-tab>Categories List</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- Onglet 1 : Formulaire d'ajout -->
        <v-tab-item>
          <v-card flat class="pa-6">
            <v-form @submit.prevent="submitCategory" ref="form">
              <v-text-field
                v-model="name"
                label="Category Name"
                outlined
                dense
                rounded
                filled
                background-color="grey lighten-4"
                prepend-inner-icon="fas fa-tag"
                :rules="[v => !!v.trim() || 'Name is required']"
                required
                :disabled="loading"
              ></v-text-field>

              <v-btn
                type="submit"
                color="primary"
                elevation="2"
                rounded
                class="text-none font-weight-bold px-8"
                :loading="loading"
                :disabled="!name.trim()"
              >
                <i class="fas fa-plus white--text" style="margin-right: 8px;"></i>
                Save Category
              </v-btn>
            </v-form>
          </v-card>
        </v-tab-item>

        <!-- Onglet 2 : Liste des catégories -->
        <v-tab-item>
          <v-card flat class="pa-4">
            <!-- Barre de recherche -->
            <v-text-field
              v-model="search"
              append-icon="fas fa-search"
              label="Search categories..."
              single-line
              hide-details
              dense
              filled
              rounded
              class="mb-4"
              background-color="grey lighten-4"
            ></v-text-field>

            <v-data-table
              :headers="headers"
              :items="categories"
              :items-per-page="10"
              :search="search"
              :loading="loadingList"
              loading-text="Loading categories..."
              no-data-text="No categories found."
              class="elevation-0"
            >
              <!-- Nom de la catégorie -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-2">
                  <i class="fas fa-tag primary--text" style="font-size: 18px; margin-right: 12px;"></i>
                  <span class="font-weight-medium">{{ item.name }}</span>
                </div>
              </template>

              <!-- Date de création -->
              <template v-slot:item.get_date_str="{ item }">
                <span class="text-caption grey--text">{{ item.get_date_str }}</span>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDelete(item)"
                  class="hover-scale"
                >
                  <i class="fas fa-trash-alt error--text" style="font-size: 16px;"></i>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Dialogue de confirmation de suppression -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card class="rounded-xl pa-2">
        <v-card-text class="text-center pt-6 pb-2">
          <i class="fas fa-trash-alt error--text" style="font-size: 40px; margin-bottom: 16px;"></i>
          <h3 class="text-h6 font-weight-bold mb-2">Delete Category?</h3>
          <p class="text-body-2 grey--text text--darken-1">
            Are you sure you want to delete <strong>"{{ categoryToDelete ? categoryToDelete.name : '' }}"</strong>?<br>
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text rounded class="text-none font-weight-bold" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            rounded
            class="text-none font-weight-bold px-6"
            @click="deleteCategory"
            :loading="deleting"
            elevation="2"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      tab: 0,
      name: '',
      loading: false,
      loadingList: false,
      categories: [],
      search: '',
      deleteDialog: false,
      categoryToDelete: null,
      deleting: false,
      headers: [
        { text: 'Name', value: 'name', width: '70%' },
        { text: 'Created', value: 'get_date_str', width: '20%' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end', width: '10%' },
      ],
    }
  },

  mounted() {
    document.title = "Manager: Categories"
    this.fetchCategories()
  },

  methods: {
    async fetchCategories() {
      this.loadingList = true
      try {
        const response = await axios.get('/api/v1/manager/market_place/categories/')
        this.categories = response.data
      } catch (error) {
        this.handleError(error, 'Failed to load categories')
      } finally {
        this.loadingList = false
      }
    },

    async submitCategory() {
      if (!this.name.trim()) return

      this.loading = true
      try {
        await axios.post('/api/v1/manager/market_place/categories/', {
          name: this.name.trim(),
        })
        this.$store.commit('setSnackBarMessage', 'Category added successfully')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        this.name = ''
        this.$refs.form.resetValidation()
        await this.fetchCategories()
        this.tab = 1
      } catch (error) {
        this.handleError(error, 'Error adding category')
      } finally {
        this.loading = false
      }
    },

    confirmDelete(category) {
      this.categoryToDelete = category
      this.deleteDialog = true
    },

    async deleteCategory() {
      if (!this.categoryToDelete) return
      this.deleting = true
      try {
        await axios.delete(`/api/v1/manager/market_place/categories/${this.categoryToDelete.id}/`)
        this.$store.commit('setSnackBarMessage', 'Category deleted successfully')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        this.deleteDialog = false
        this.categoryToDelete = null
        await this.fetchCategories()
      } catch (error) {
        this.handleError(error, 'Error deleting category')
      } finally {
        this.deleting = false
      }
    },

    handleError(error, defaultMessage) {
      let message = defaultMessage
      if (error.response && error.response.data && error.response.data.error) {
        message = error.response.data.error
      } else if (error.response) {
        message = `${error.response.status} ${error.response.statusText}`
      }
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', 'error')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.hover-scale {
  transition: transform 0.2s ease;
}
.hover-scale:hover {
  transform: scale(1.15);
}
</style>